import type { DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import type { Buoc6MucTieu, Buoc6KPI } from '@/types/performance/buoc6phuongdien';
import type {
  EmployeeHomeCanLamGoalGroup,
  EmployeeHomeCanLamSection,
  EmployeeHomeKpiReadonly,
  EmployeeHomeManagingGoalGroup,
  EmployeeHomeManagingItem,
  EmployeeHomeManagingSection,
  EmployeeHomeMucTieuGroup,
  EmployeeHomeResponse,
  EmployeeHomeSectionPagination,
  EmployeeHomeTheoMucTieuSection,
  EmployeeHomeStats,
  ManagerHomeResponse,
  EmployeeHomeSummaryResponse,
} from '@/types/action-plan-v2/EmployeeHome';
import type { UnitHelpers } from '@/components/MeasureIndicators/helpers/unitHelpers';
import { getManagingItemTanSuatState, isCompletedTask, isOverdueTask, type TanSuatStateGroup } from '@/components/common/ActionPlanV2/core/taskStatus';
import { flattenLeafTasks } from '@/components/common/ActionPlanV2/core/tree';
import type { PathTreeItem } from '@/components/common/ActionPlanV2/core/tree';
import { isValidMucTieuId, mapMucTieuFilterFromApiPayload, type MucTieuFilterData } from '@/components/common/ActionPlanV2/core/mucTieu';
import { actionPlanV2Api } from '@/services/action-plan-v2';
import { ACTION_PLAN_V2 } from '@/components/common/ActionPlanV2/constants';

// #region read

/** Đọc payload API lỏng (camelCase / PascalCase) — leaf. */

export const readRecord = (raw: unknown): Record<string, unknown> | null =>
  raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;

// #endregion read

// #region employeeHomeApi

/** Bucket trạng thái từ BE (gồm completed). */
export type EmployeeHomeBeStateGroup = TanSuatStateGroup;

export const MANAGING_SECTION_KEYS: EmployeeHomeBeStateGroup[] = [
  'overdue',
  'today',
  'upcoming',
  'inprogress',
  'completed',
];

export const emptyManagingSection = (): EmployeeHomeManagingSection => ({
  overdue: [],
  today: [],
  upcoming: [],
  inprogress: [],
  completed: [],
});

export const emptyCanLamSection = (): EmployeeHomeCanLamSection => ({
  overdue: [],
  today: [],
  upcoming: [],
  inprogress: [],
  completed: [],
});

export const emptyTheoMucTieuSection = (): EmployeeHomeTheoMucTieuSection => ({
  items: [],
  total: 0,
  pageCurrent: 1,
  pageSize: 0,
  hasMore: false,
});

export const getTheoMucTieuItems = (
  section?: EmployeeHomeTheoMucTieuSection | EmployeeHomeMucTieuGroup[] | null
): EmployeeHomeMucTieuGroup[] => {
  if (!section) return [];
  if (Array.isArray(section)) return section;
  return section.items ?? [];
};

export const getTheoMucTieuListPagination = (
  section?: EmployeeHomeTheoMucTieuSection | null
): EmployeeHomeSectionPagination | undefined => {
  if (!section) return undefined;
  return {
    total: section.total,
    pageCurrent: section.pageCurrent,
    pageSize: section.pageSize,
    hasMore: section.hasMore,
  };
};

export const resolveSectionHasMore = (
  pagination: EmployeeHomeSectionPagination | undefined,
  loadedCount: number
): boolean => {
  if (!pagination) return false;
  if (pagination.hasMore) return true;
  if (pagination.total > 0 && loadedCount < pagination.total) return true;
  return false;
};

export const toEmployeeHomeTrangThaiApi = (
  state: TanSuatStateGroup
): 'overdue' | 'today' | 'upcoming' | 'inProgress' | 'completed' => {
  if (state === 'inprogress') return 'inProgress';
  if (state === 'completed') return 'completed';
  return state;
};

export const toEmployeeHomeTabApi = (
  workspaceView: 'tasks' | 'managed' | 'goals'
): 'tasks' | 'managed' | 'goals' => workspaceView;

function flattenManagingItems(
  section?: EmployeeHomeManagingSection | null
): EmployeeHomeManagingItem[] {
  if (!section) return [];
  return MANAGING_SECTION_KEYS.flatMap((key) =>
    (section[key] ?? []).flatMap((goal) => goal.items ?? [])
  );
}

export function countManagingSectionTasks(
  section: EmployeeHomeManagingSection,
  state: TanSuatStateGroup
): number {
  return (section[state] ?? []).reduce(
    (sum, goal) => sum + (goal.total > 0 ? goal.total : goal.items.length),
    0
  );
}

export function groupManagingItemsByGoal(
  items: EmployeeHomeManagingItem[]
): EmployeeHomeManagingGoalGroup[] {
  const map = new Map<string, EmployeeHomeManagingGoalGroup>();

  items.forEach((item) => {
    const mtctid = String(item.node?.congViec?.mtctid || '_none');
    const existing = map.get(mtctid);
    if (existing) {
      existing.items.push(item);
      existing.total = existing.items.length;
      return;
    }
    map.set(mtctid, {
      mtctid,
      mucTieuName: item.mucTieuName,
      hasMore: false,
      pageCurrent: 1,
      pageSize: 1,
      total: 1,
      items: [item],
    });
  });

  return Array.from(map.values());
}

/**
 * Gom dangQuanLy (đã giao) theo mtctid → EmployeeHomeMucTieuGroup
 * để dùng chung với UI theoMucTieu (card Mục tiêu phụ trách / grid NV).
 */
export function mapDangQuanLyToMucTieuGroups(
  section?: EmployeeHomeManagingSection | null
): EmployeeHomeMucTieuGroup[] {
  const items = flattenManagingItems(section);
  if (!items.length) return [];

  const byMtctid = new Map<
    string,
    {
      mucTieuName?: string;
      tasks: DuAnCongViecTree[];
      boPhanPhuTrachID?: string;
    }
  >();

  items.forEach((item) => {
    const node = item.node;
    if (!node) return;
    const mtctid = String(node.congViec?.mtctid || '').trim();
    if (!isValidMucTieuId(mtctid)) return;
    const existing = byMtctid.get(mtctid);
    if (existing) {
      existing.tasks.push(node);
      if (!existing.mucTieuName && item.mucTieuName) existing.mucTieuName = item.mucTieuName;
      if (!existing.boPhanPhuTrachID && node.congViec?.boPhanPhuTrachID) {
        existing.boPhanPhuTrachID = String(node.congViec.boPhanPhuTrachID).trim();
      }
      return;
    }
    byMtctid.set(mtctid, {
      mucTieuName: item.mucTieuName || undefined,
      tasks: [node],
      boPhanPhuTrachID: String(node.congViec?.boPhanPhuTrachID || '').trim() || undefined,
    });
  });

  return Array.from(byMtctid.entries()).map(([mtctid, g]) => {
    const leaves = flattenLeafTasks(g.tasks);
    let done = 0;
    let overdue = 0;
    leaves.forEach((task) => {
      if (isCompletedTask(task)) done += 1;
      else if (isOverdueTask(task)) overdue += 1;
    });
    return {
      mtctid,
      mucTieuName: g.mucTieuName || mtctid,
      kpis: [],
      tasks: g.tasks,
      done,
      total: leaves.length,
      overdue,
      boPhanPhuTrachID: g.boPhanPhuTrachID,
    };
  });
}

export function groupCanLamTasksByGoal(
  tasks: DuAnCongViecTree[],
  mucTieuNameMap?: Map<string, string>
): EmployeeHomeCanLamGoalGroup[] {
  const map = new Map<string, EmployeeHomeCanLamGoalGroup>();

  tasks.forEach((task) => {
    const mtctid = String(task.congViec?.mtctid || '_none');
    const existing = map.get(mtctid);
    if (existing) {
      existing.tasks.push(task);
      existing.total = existing.tasks.length;
      return;
    }
    map.set(mtctid, {
      mtctid,
      mucTieuName: mucTieuNameMap?.get(mtctid),
      hasMore: false,
      pageCurrent: 1,
      pageSize: 1,
      total: 1,
      tasks: [task],
    });
  });

  return Array.from(map.values());
}

export function countCanLamSectionTasks(
  section: EmployeeHomeCanLamSection,
  state: TanSuatStateGroup
): number {
  return (section[state] ?? []).reduce(
    (sum, goal) => sum + (goal.total > 0 ? goal.total : goal.tasks.length),
    0
  );
}

export function normalizeDuAnCongViecTreeNode(raw: unknown): DuAnCongViecTree {
  const r = readRecord(raw);
  if (!r) return raw as DuAnCongViecTree;

  const cvRaw = readRecord(r.congViec ?? r.CongViec);
  const tenCongViecParent = String(
    cvRaw?.tenCongViecParent
    ?? cvRaw?.TenCongViecParent
    ?? r.tenCongViecParent
    ?? r.TenCongViecParent
    ?? ''
  ).trim();
  const childsRaw = r.childs ?? r.Childs;
  const childs = Array.isArray(childsRaw)
    ? childsRaw.map(normalizeDuAnCongViecTreeNode)
    : [];

  const node = { ...(raw as DuAnCongViecTree) };
  if (cvRaw) {
    const ngayTaoRaw = cvRaw.ngayTao ?? cvRaw.NgayTao;
    node.congViec = {
      ...(cvRaw as unknown as DuAnCongViecTree['congViec']),
      id: String(cvRaw.id ?? cvRaw.Id ?? node.congViec?.id ?? ''),
      tenCongViec: String(cvRaw.tenCongViec ?? cvRaw.TenCongViec ?? node.congViec?.tenCongViec ?? ''),
      ...(ngayTaoRaw != null && ngayTaoRaw !== '' ? { ngayTao: ngayTaoRaw as Date } : {}),
      ...(tenCongViecParent ? { tenCongViecParent } : {}),
    };
  } else if (tenCongViecParent) {
    node.congViec = {
      ...(node.congViec ?? ({} as DuAnCongViecTree['congViec'])),
      tenCongViecParent,
    };
  }
  node.childs = childs;
  return node;
}

export function normalizeDuAnCongViecTreeList(raw: unknown): DuAnCongViecTree[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeDuAnCongViecTreeNode);
}

export function readCongViecTreePathName(res: unknown): string {
  const root = readRecord(res);
  if (!root) return '';
  const nested = readRecord(root.data ?? root.Data);
  const data = nested ?? root;
  return String(data.treePathName ?? data.TreePathName ?? '').trim();
}

type ParentLinkedItem = PathTreeItem & { parentId: string };

/** Xếp danh sách phẳng theo chuỗi cha → con; node lạc (parent không có trong list) giữ nguyên thứ tự. */
function orderByParentChain(items: ParentLinkedItem[]): ParentLinkedItem[] {
  if (items.length <= 1) return items;

  const knownIds = new Set(items.map((item) => item.id).filter(Boolean));
  const byParent = new Map<string, ParentLinkedItem[]>();
  items.forEach((item) => {
    const key = item.parentId && knownIds.has(item.parentId) ? item.parentId : '';
    const bucket = byParent.get(key);
    if (bucket) bucket.push(item);
    else byParent.set(key, [item]);
  });

  const ordered: ParentLinkedItem[] = [];
  const visited = new Set<ParentLinkedItem>();
  const walk = (parentKey: string) => {
    (byParent.get(parentKey) ?? []).forEach((item) => {
      if (visited.has(item)) return;
      visited.add(item);
      ordered.push(item);
      if (item.id) walk(item.id);
    });
  };
  walk('');

  items.forEach((item) => {
    if (!visited.has(item)) ordered.push(item);
  });
  return ordered;
}

/** Đọc cây tổ tiên công việc từ API cong-viec-tree-path (gốc → … → cha trực tiếp). */
export function readCongViecTreePathItems(res: unknown): PathTreeItem[] {
  const root = readRecord(res);
  if (!root) return [];
  const nested = readRecord(root.data ?? root.Data);
  const data = nested ?? root;
  const listRaw = data.treePathCongViecList ?? data.TreePathCongViecList;
  if (!Array.isArray(listRaw)) return [];

  const items = listRaw
    .map((raw): ParentLinkedItem | null => {
      const row = readRecord(raw);
      if (!row) return null;
      return {
        id: String(row.id ?? row.Id ?? '').trim(),
        ten: String(row.tenCongViec ?? row.TenCongViec ?? row.ten ?? row.Ten ?? '').trim(),
        parentId: String(row.parentID ?? row.ParentID ?? '').trim(),
      };
    })
    .filter((item): item is ParentLinkedItem => Boolean(item?.ten || item?.id));

  return orderByParentChain(items).map(({ id, ten }) => ({ id, ten }));
}

/** Đọc danh sách cấp mục tiêu từ API muctieu-parents (root → … → bản thân). */
export function readMucTieuParentsPathItems(res: unknown): PathTreeItem[] {
  const root = readRecord(res);
  if (!root) return [];
  const nested = readRecord(root.data ?? root.Data);
  const data = nested ?? root;
  const pathRaw = data.path ?? data.Path;
  if (!Array.isArray(pathRaw) || pathRaw.length === 0) {
    const ten = String(data.ten ?? data.Ten ?? '').trim();
    const id = String(data.id ?? data.Id ?? '').trim();
    return ten || id ? [{ id, ten }] : [];
  }
  return pathRaw
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        id: String(row.id ?? row.Id ?? '').trim(),
        ten: String(row.ten ?? row.Ten ?? '').trim(),
      };
    })
    .filter((item) => Boolean(item.ten || item.id));
}

const appendUniqueTasks = (
  current: DuAnCongViecTree[],
  incoming: DuAnCongViecTree[]
): DuAnCongViecTree[] => {
  if (!incoming.length) return current;
  const seen = new Set(
    current.map((item) => item.congViec?.id).filter((id): id is string => Boolean(id))
  );
  const next = [...current];
  incoming.forEach((item) => {
    const id = item.congViec?.id;
    if (!id || seen.has(id)) return;
    seen.add(id);
    next.push(item);
  });
  return next;
};

export const mergeCanLamGoalPage = (
  prev: EmployeeHomeResponse,
  partial: EmployeeHomeResponse,
  state: TanSuatStateGroup,
  mtctid: string
): EmployeeHomeResponse => {
  const partialGoal = partial.canLamHomNay[state].find((goal) => goal.mtctid === mtctid);
  if (!partialGoal) return prev;

  const nextSection = { ...prev.canLamHomNay };
  nextSection[state] = prev.canLamHomNay[state].map((goal) => {
    if (goal.mtctid !== mtctid) return goal;
    const mergedTasks = appendUniqueTasks(goal.tasks, partialGoal.tasks);
    return {
      ...goal,
      ...partialGoal,
      tasks: mergedTasks,
      hoanThanhTreNgay: { ...goal.hoanThanhTreNgay, ...partialGoal.hoanThanhTreNgay },
      total: partialGoal.total > 0 ? partialGoal.total : mergedTasks.length,
    };
  });

  if (!nextSection[state].some((goal) => goal.mtctid === mtctid)) {
    nextSection[state] = [...nextSection[state], partialGoal];
  }

  return {
    ...prev,
    canLamHomNay: nextSection,
  };
};

export function buildManagingItemFromNode(
  node: DuAnCongViecTree,
  mucTieuName?: string
): EmployeeHomeManagingItem {
  const leaves = flattenLeafTasks([node]);
  const total = leaves.length;
  const done = leaves.filter(isCompletedTask).length;
  const overdue = leaves.filter((item) => !isCompletedTask(item) && isOverdueTask(item)).length;
  const rootProgress = node.trongKy?.hoanThanh;

  return {
    node,
    mucTieuName,
    total,
    done,
    overdue,
    hoanThanhPct:
      typeof rootProgress === 'number' && Number.isFinite(rootProgress)
        ? rootProgress
        : total > 0
          ? Math.round((done / total) * 100)
          : 0,
  };
}

/** Chuyển list legacy (API cũ) sang section theo trạng thái + nhóm mục tiêu. */
export function buildManagingSectionFromItems(
  items: EmployeeHomeManagingItem[],
  todayTaskIdSet?: Set<string>
): EmployeeHomeManagingSection {
  const byState: Record<TanSuatStateGroup, EmployeeHomeManagingItem[]> = {
    overdue: [],
    today: [],
    upcoming: [],
    inprogress: [],
    completed: [],
  };
  const seen = new Set<string>();

  items.forEach((item) => {
    const id = item.node?.congViec?.id;
    if (!id || seen.has(id)) return;
    seen.add(id);
    const state = getManagingItemTanSuatState(item.node, todayTaskIdSet);
    if (state === 'completed') return;
    byState[state].push(item);
  });

  return {
    overdue: groupManagingItemsByGoal(byState.overdue),
    today: groupManagingItemsByGoal(byState.today),
    upcoming: groupManagingItemsByGoal(byState.upcoming),
    inprogress: groupManagingItemsByGoal(byState.inprogress),
    completed: groupManagingItemsByGoal(byState.completed),
  };
}

const appendUniqueManagingItems = (
  current: EmployeeHomeManagingItem[],
  incoming: EmployeeHomeManagingItem[]
): EmployeeHomeManagingItem[] => {
  if (!incoming.length) return current;
  const seen = new Set(
    current.map((item) => item.node?.congViec?.id).filter((id): id is string => Boolean(id))
  );
  const next = [...current];
  incoming.forEach((item) => {
    const id = item.node?.congViec?.id;
    if (!id || seen.has(id)) return;
    seen.add(id);
    next.push(item);
  });
  return next;
};

export const mergeManagedGoalPage = (
  prev: EmployeeHomeResponse,
  partial: EmployeeHomeResponse,
  state: TanSuatStateGroup,
  mtctid: string
): EmployeeHomeResponse => {
  const partialGoal = partial.dangQuanLy[state].find((goal) => goal.mtctid === mtctid);
  if (!partialGoal) return prev;

  const nextSection = { ...prev.dangQuanLy };
  nextSection[state] = prev.dangQuanLy[state].map((goal) => {
    if (goal.mtctid !== mtctid) return goal;
    const mergedItems = appendUniqueManagingItems(goal.items, partialGoal.items);
    return {
      ...goal,
      ...partialGoal,
      items: mergedItems,
      total: partialGoal.total > 0 ? partialGoal.total : mergedItems.length,
    };
  });

  if (!nextSection[state].some((goal) => goal.mtctid === mtctid)) {
    nextSection[state] = [...nextSection[state], partialGoal];
  }

  return {
    ...prev,
    dangQuanLy: nextSection,
  };
};

export const mergeTheoMucTieuListPage = (
  prev: EmployeeHomeResponse,
  partial: EmployeeHomeResponse
): EmployeeHomeResponse => {
  const partialItems = getTheoMucTieuItems(partial.theoMucTieu);
  if (!partialItems.length) return prev;

  const prevItems = getTheoMucTieuItems(prev.theoMucTieu);
  const seen = new Set(prevItems.map((group) => group.mtctid));
  const appended = partialItems.filter((group) => !seen.has(group.mtctid));
  const mergedItems = [...prevItems, ...appended];

  return {
    ...prev,
    theoMucTieu: {
      items: mergedItems,
      total: partial.theoMucTieu.total || prev.theoMucTieu.total,
      pageCurrent: partial.theoMucTieu.pageCurrent || prev.theoMucTieu.pageCurrent,
      pageSize: partial.theoMucTieu.pageSize || prev.theoMucTieu.pageSize,
      hasMore: partial.theoMucTieu.hasMore ?? prev.theoMucTieu.hasMore,
    },
  };
};

export const mergeTheoMucTieuGoalPage = (
  prev: EmployeeHomeResponse,
  partial: EmployeeHomeResponse,
  mtctid: string
): EmployeeHomeResponse => {
  const normalizedMtctid = String(mtctid || '').trim().toLowerCase();
  const partialGoal = getTheoMucTieuItems(partial.theoMucTieu).find(
    (group) => String(group.mtctid || '').trim().toLowerCase() === normalizedMtctid
  );
  if (!partialGoal) return prev;

  return {
    ...prev,
    theoMucTieu: {
      ...prev.theoMucTieu,
      items: getTheoMucTieuItems(prev.theoMucTieu).map((goal) => {
        if (String(goal.mtctid || '').trim().toLowerCase() !== normalizedMtctid) return goal;
        const mergedTasks = appendUniqueTasks(goal.tasks, partialGoal.tasks);
        return {
          ...goal,
          ...partialGoal,
          tasks: mergedTasks,
          total: partialGoal.total > 0 ? partialGoal.total : mergedTasks.length,
        };
      }),
    },
  };
};

function readNonEmptyString(value: unknown): string | undefined {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function readChiTieuValue(value: unknown): number | string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = String(value).trim();
  return text || undefined;
}

export function mapBuoc6KpiToEmployeeHomeKpi(kpi: Buoc6KPI | Record<string, unknown>): EmployeeHomeKpiReadonly {
  const raw = kpi as Record<string, unknown>;
  const chiTieuContainer = raw.chiTieu ?? raw.ChiTieu;

  let chiTieuValue: number | string | undefined;
  let idDoLuong: string | undefined;

  if (chiTieuContainer != null && typeof chiTieuContainer === 'object') {
    const ct = chiTieuContainer as Record<string, unknown>;
    chiTieuValue = readChiTieuValue(ct.chiTieu ?? ct.ChiTieu);
    idDoLuong = readNonEmptyString(ct.idDoLuong ?? ct.IdDoLuong);
  } else {
    chiTieuValue = readChiTieuValue(chiTieuContainer);
  }

  if (!idDoLuong) {
    idDoLuong = readNonEmptyString(raw.iddoluong ?? raw.idDoLuong ?? raw.IdDoLuong);
  }

  const listTrongKy = raw.listtrongky ?? raw.listTrongKy;
  let thucHienValue: number | string | undefined;
  if (listTrongKy != null && typeof listTrongKy === 'object') {
    const lk = listTrongKy as Record<string, unknown>;
    thucHienValue = readChiTieuValue(lk.thucTe ?? lk.thucTeHienThi ?? lk.hieuSuatThucTe);
    if (chiTieuValue == null) {
      chiTieuValue = readChiTieuValue(lk.chiTieu);
    }
  }
  if (thucHienValue == null) {
    thucHienValue = readChiTieuValue(raw.thucHien ?? raw.thucTe ?? raw.thucTeHienThi);
  }

  const donVi = readNonEmptyString(
    raw.tendonvi ?? raw.tenDonVi ?? raw.TenDonVi ?? raw.donVi ?? raw.DonVi,
  );

  return {
    kpiID: readNonEmptyString(raw.kpiID ?? raw.KpiID),
    kpiName: readNonEmptyString(raw.kpiName ?? raw.KpiName),
    kpiTiTrongDatDuoc: (raw.kpititrongdatduoc ?? raw.kpiTiTrongDatDuoc) as number | undefined,
    chiTieuValue,
    thucHienValue,
    idDoLuong,
    donVi,
  };
}

export function mergeEmployeeHomeKpiMeta(
  base: EmployeeHomeKpiReadonly,
  extra?: EmployeeHomeKpiReadonly | null,
): EmployeeHomeKpiReadonly {
  if (!extra) return base;
  return {
    kpiID: base.kpiID || extra.kpiID,
    kpiName: base.kpiName || extra.kpiName,
    kpiTiTrongDatDuoc: base.kpiTiTrongDatDuoc ?? extra.kpiTiTrongDatDuoc,
    chiTieuValue: base.chiTieuValue ?? extra.chiTieuValue,
    thucHienValue: base.thucHienValue ?? extra.thucHienValue,
    idDoLuong: base.idDoLuong || extra.idDoLuong,
    donVi: base.donVi || extra.donVi,
  };
}

export function indexKpisFromBscPerspectives(
  perspectives: Array<{ listMucTieu?: Array<{ muctieuID?: string; listKPI?: unknown[] }> }> | null | undefined,
): Map<string, Map<string, EmployeeHomeKpiReadonly>> {
  const index = new Map<string, Map<string, EmployeeHomeKpiReadonly>>();
  (perspectives ?? []).forEach((pd) => {
    pd.listMucTieu?.forEach((mt) => {
      const mtctid = readNonEmptyString(mt.muctieuID);
      if (!mtctid) return;
      const byKpi = index.get(mtctid) ?? new Map<string, EmployeeHomeKpiReadonly>();
      (mt.listKPI ?? []).forEach((kpi) => {
        const mapped = mapBuoc6KpiToEmployeeHomeKpi(kpi as Buoc6KPI);
        const kpiID = mapped.kpiID;
        if (!kpiID) return;
        byKpi.set(kpiID, mergeEmployeeHomeKpiMeta(byKpi.get(kpiID) ?? {}, mapped));
      });
      index.set(mtctid, byKpi);
    });
  });
  return index;
}

function splitKpiNameUnitSuffix(name: string): { baseName: string; unitFromName: string } {
  const match = name.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (!match) return { baseName: name.trim(), unitFromName: '' };
  return { baseName: match[1].trim(), unitFromName: match[2].trim() };
}

function formatChiTieuValue(value: number | string): string {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return num.toLocaleString('vi-VN', { maximumFractionDigits: 3 });
  }
  return String(value);
}

export function formatEmployeeHomeKpiMetricValue(
  kpi: EmployeeHomeKpiReadonly,
  value: number | string | undefined,
  unitHelpers?: UnitHelpers | null,
): string {
  if (value == null || value === '') return '';
  const idDoLuong = kpi.idDoLuong;
  const isThangDo = !!(idDoLuong && unitHelpers?.isThangDoUnit(idDoLuong));
  if (isThangDo && idDoLuong && unitHelpers) {
    return unitHelpers.getChildUnitLabel(idDoLuong, String(value));
  }
  return formatChiTieuValue(value);
}

export function resolveEmployeeHomeKpiUnit(
  kpi: EmployeeHomeKpiReadonly,
  unitHelpers?: UnitHelpers | null,
): string {
  const { unitFromName } = splitKpiNameUnitSuffix((kpi.kpiName || '').trim());
  const idDoLuong = kpi.idDoLuong;
  const resolved =
    kpi.donVi?.trim()
    || (idDoLuong && unitHelpers?.getUnitLabel(idDoLuong))
    || unitFromName
    || '';
  if (resolved) return resolved;

  // Không có đơn vị → lấy đơn vị đầu tiên trong IndexedDB
  const firstUnit = unitHelpers?.transformUnitsToOptions()?.[0];
  return String(firstUnit?.label || '').trim();
}

export type GoalKpiTaskSection = {
  kpi: EmployeeHomeKpiReadonly | null;
  tasks: DuAnCongViecTree[];
};

/** Mục tiêu → KPI → công việc (gắn qua congViec.lienKetID = kpiID). */
export function groupGoalTasksByKpi(group: EmployeeHomeMucTieuGroup): GoalKpiTaskSection[] {
  const kpis = group.kpis ?? [];
  const leaves = flattenLeafTasks(group.tasks);
  const kpiIdSet = new Set(kpis.map((k) => k.kpiID).filter(Boolean) as string[]);
  const byKpi = new Map<string, DuAnCongViecTree[]>();
  const unlinked: DuAnCongViecTree[] = [];

  leaves.forEach((task) => {
    const link = String(task.congViec?.lienKetID || '').trim();
    if (link && kpiIdSet.has(link)) {
      const list = byKpi.get(link) ?? [];
      list.push(task);
      byKpi.set(link, list);
    } else {
      unlinked.push(task);
    }
  });

  const sections: GoalKpiTaskSection[] = kpis.map((kpi) => ({
    kpi,
    tasks: kpi.kpiID ? (byKpi.get(kpi.kpiID) ?? []) : [],
  }));

  if (unlinked.length > 0) {
    if (kpis.length === 1) {
      sections[0].tasks.push(...unlinked);
    } else if (kpis.length === 0) {
      sections.push({ kpi: null, tasks: unlinked });
    } else {
      sections.push({ kpi: null, tasks: unlinked });
    }
  }

  return sections.length > 0 ? sections : [{ kpi: null, tasks: leaves }];
}

export function buildGoalNameMapFromEmployeeHome(
  data: {
    theoMucTieu?: EmployeeHomeTheoMucTieuSection | EmployeeHomeMucTieuGroup[] | null;
    dangQuanLy?: EmployeeHomeManagingSection | null;
  } | null
): Map<string, string> {
  const map = new Map<string, string>();
  if (!data) return map;
  getTheoMucTieuItems(data.theoMucTieu).forEach((group) => {
    if (group.mtctid) map.set(group.mtctid, group.mucTieuName || group.mtctid);
  });
  if (data.dangQuanLy) {
    MANAGING_SECTION_KEYS.forEach((state) => {
      data.dangQuanLy?.[state]?.forEach((group) => {
        if (group.mtctid) {
          map.set(group.mtctid, group.mucTieuName || group.mtctid);
        }
      });
    });
  }
  return map;
}

/** Gom toàn bộ việc lá từ workTask (theoMucTieu) — dedupe theo id. */
export function collectWorkTasksFromEmployeeHome(
  data: { theoMucTieu?: EmployeeHomeTheoMucTieuSection | EmployeeHomeMucTieuGroup[] | null } | null
): DuAnCongViecTree[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: DuAnCongViecTree[] = [];

  const add = (item?: DuAnCongViecTree) => {
    const id = item?.congViec?.id;
    if (!item || !id || seen.has(id)) return;
    seen.add(id);
    out.push(item);
  };

  getTheoMucTieuItems(data.theoMucTieu).forEach((g) => g.tasks?.forEach(add));
  return out;
}

const upsertMucTieuName = (
  map: Map<string, Buoc6MucTieu>,
  mtctid: string,
  mucTieuName?: string
) => {
  const id = String(mtctid || '').trim();
  if (!id) return;
  const name = String(mucTieuName || '').trim();
  const resolved =
    name && name !== id && name.toLowerCase() !== id.toLowerCase() ? name : '';
  const existing = map.get(id);
  if (!existing) {
    map.set(id, {
      muctieuID: id,
      muctieuName: resolved || id,
      muctieutitrong: 0,
      muctieutitrongdatduoc: 0,
      phantramhoanthanh: 0,
      muctieuRowSpan: 1,
      muctieuhoanthanhtrongky: 0,
      muctieuhoanthanhcanam: 0,
      iconTrongKy: '',
      listKPI: [],
      congViecs: [],
    } as Buoc6MucTieu);
    return;
  }
  const existingName = String(existing.muctieuName || '').trim();
  const existingIsId =
    !existingName ||
    existingName === id ||
    existingName.toLowerCase() === id.toLowerCase();
  if (resolved && existingIsId) {
    map.set(id, { ...existing, muctieuName: resolved });
  }
};

export function buildMucTieuMapFromEmployeeHome(
  data: EmployeeHomeResponse | null
): Map<string, Buoc6MucTieu> {
  const map = new Map<string, Buoc6MucTieu>();
  if (!data) return map;

  getTheoMucTieuItems(data.theoMucTieu).forEach((g) => {
    if (!g.mtctid) return;
    map.set(g.mtctid, {
      muctieuID: g.mtctid,
      muctieuName: g.mucTieuName ?? g.mtctid,
      muctieutitrong: 0,
      muctieutitrongdatduoc: 0,
      phantramhoanthanh: 0,
      muctieuRowSpan: 1,
      muctieuhoanthanhtrongky: 0,
      muctieuhoanthanhcanam: 0,
      iconTrongKy: '',
      listKPI: (g.kpis ?? []).map((k) => ({
        kpiID: k.kpiID ?? '',
        kpiName: k.kpiName ?? '',
        kpititrongdatduoc: k.kpiTiTrongDatDuoc,
        chiTieu: k.chiTieuValue != null ? { chiTieu: Number(k.chiTieuValue) || 0, idDoLuong: k.idDoLuong } : undefined,
        iddoluong: k.idDoLuong,
        tendonvi: k.donVi,
      })) as Buoc6KPI[],
      congViecs: [],
    } as Buoc6MucTieu);
  });

  // Bổ sung tên từ canLam / dangQuanLy (mục tiêu ngoài phụ trách thường chỉ có ở đây).
  MANAGING_SECTION_KEYS.forEach((state) => {
    data.canLamHomNay?.[state]?.forEach((goal) => {
      upsertMucTieuName(map, goal.mtctid, goal.mucTieuName);
    });
    data.dangQuanLy?.[state]?.forEach((goal) => {
      upsertMucTieuName(map, goal.mtctid, goal.mucTieuName);
      goal.items?.forEach((item) => {
        upsertMucTieuName(
          map,
          item.node?.congViec?.mtctid || goal.mtctid,
          item.mucTieuName || goal.mucTieuName
        );
      });
    });
  });

  return map;
}

// #endregion employeeHomeApi

// #region loadManagerEmployeeHome

export type LoadManagerEmployeeHomeParams = {
  scopeId: string;
  idChoice: string;
  year: number;
  /** "DD/MM/YYYY - DD/MM/YYYY" — lọc theo khoảng thời gian (từ filter). */
  dateChoice?: string;
  /**
   * Cách lọc DateChoice. Mặc định `created` khi có dateChoice (việc tạo trong kỳ).
   * overlap | created | started | ended.
   */
  dateFilterMode?: 'overlap' | 'created' | 'started' | 'ended';
  /** Phòng: load MT phụ trách. NV: BE bỏ qua. */
  includeMtPhuTrach?: boolean;
  /** Phòng: load MT không phụ trách (admin). NV: BE bỏ qua. */
  includeMtKhongPhuTrach?: boolean;
};

export const readNumber = (value: unknown): number => {
  const num = Number(value ?? 0);
  return Number.isFinite(num) ? num : 0;
};

export const normalizeStats = (raw: unknown): EmployeeHomeStats => {
  const r = readRecord(raw);
  return {
    total: readNumber(r?.total ?? r?.Total),
    done: readNumber(r?.done ?? r?.Done),
    overdue: readNumber(r?.overdue ?? r?.Overdue),
    inProgress: readNumber(r?.inProgress ?? r?.InProgress),
  };
};

// const readArrayField = (r: Record<string, unknown>, keys: string[]): unknown[] => {
//   for (const key of keys) {
//     const value = r[key];
//     if (Array.isArray(value)) return value;
//   }
//   return [];
// };

const readHasMore = (value: unknown, pageCurrent: number, pageSize: number, total: number): boolean => {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true' || value === 'True') return true;
  if (value === 0 || value === '0' || value === 'false' || value === 'False') return false;
  if (pageSize > 0 && total > 0) return pageCurrent * pageSize < total;
  return false;
};

const MANAGING_STATE_KEYS: EmployeeHomeBeStateGroup[] = [
  'overdue',
  'today',
  'upcoming',
  'inprogress',
  'completed',
];

const readManagingStateField = (
  r: Record<string, unknown>,
  state: EmployeeHomeBeStateGroup
): unknown => {
  const aliases: Record<EmployeeHomeBeStateGroup, string[]> = {
    overdue: ['overdue', 'Overdue'],
    today: ['today', 'Today'],
    upcoming: ['upcoming', 'Upcoming'],
    inprogress: ['inprogress', 'inProgress', 'Inprogress', 'InProgress'],
    completed: ['completed', 'Completed'],
  };
  for (const key of aliases[state]) {
    if (r[key] != null) return r[key];
  }
  return undefined;
};

const normalizeCanLamGoalGroup = (raw: unknown): EmployeeHomeCanLamGoalGroup => {
  const r = readRecord(raw) ?? {};
  const tasks = normalizeDuAnCongViecTreeList(r.tasks ?? r.Tasks);
  const pageCurrent = readNumber(r.pageCurrent ?? r.PageCurrent ?? 1);
  const pageSize = readNumber(r.pageSize ?? r.PageSize ?? 0);
  const total = readNumber(r.total ?? r.Total ?? tasks.length);
  const hasMore = readHasMore(r.hasMore ?? r.HasMore, pageCurrent, pageSize, total);

  return {
    mtctid: String(r.mtctid ?? r.Mtctid ?? ''),
    mucTieuName: String(r.mucTieuName ?? r.MucTieuName ?? ''),
    hasMore,
    pageCurrent: pageCurrent > 0 ? pageCurrent : 1,
    pageSize: pageSize > 0 ? pageSize : Math.max(tasks.length, 1),
    total: total > 0 ? total : tasks.length,
    tasks,
    hoanThanhTreNgay: (readRecord(r.hoanThanhTreNgay ?? r.HoanThanhTreNgay) as Record<string, number> | undefined) ?? undefined,
  };
};

const isCanLamGoalGroup = (item: unknown): boolean => {
  const r = readRecord(item);
  return Array.isArray(r?.tasks ?? r?.Tasks);
};

const normalizeLegacyCanLamStateList = (raw: unknown): EmployeeHomeCanLamGoalGroup[] => {
  const list = Array.isArray(raw) ? raw : [];
  return groupCanLamTasksByGoal(normalizeDuAnCongViecTreeList(list));
};

const normalizeCanLamStateList = (raw: unknown): EmployeeHomeCanLamGoalGroup[] => {
  const list = Array.isArray(raw) ? raw : [];
  if (!list.length) return [];
  if (isCanLamGoalGroup(list[0])) {
    return list.map(normalizeCanLamGoalGroup);
  }
  return normalizeLegacyCanLamStateList(raw);
};

export const normalizeCanLam = (raw: unknown): EmployeeHomeCanLamSection => {
  const r = readRecord(raw);
  if (!r) {
    return { overdue: [], today: [], upcoming: [], inprogress: [], completed: [] };
  }

  const section: EmployeeHomeCanLamSection = {
    overdue: [],
    today: [],
    upcoming: [],
    inprogress: [],
    completed: [],
  };
  MANAGING_STATE_KEYS.forEach((state) => {
    section[state] = normalizeCanLamStateList(readManagingStateField(r, state));
  });
  return section;
};

const normalizeManagingList = (raw: unknown): EmployeeHomeManagingItem[] => {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => {
    const r = readRecord(item) ?? {};
    const node = normalizeDuAnCongViecTreeNode(r.node ?? r.Node ?? item);
    return {
      node,
      mucTieuName: String(r.mucTieuName ?? r.MucTieuName ?? ''),
      total: readNumber(r.total ?? r.Total),
      done: readNumber(r.done ?? r.Done),
      overdue: readNumber(r.overdue ?? r.Overdue),
      hoanThanhPct: readNumber(r.hoanThanhPct ?? r.HoanThanhPct),
    };
  });
};

const isManagingGoalGroup = (item: unknown): boolean => {
  const r = readRecord(item);
  return Array.isArray(r?.items ?? r?.Items);
};

const normalizeManagingGoalGroup = (raw: unknown): EmployeeHomeManagingGoalGroup => {
  const r = readRecord(raw) ?? {};
  const items = normalizeManagingList(r.items ?? r.Items);
  const pageCurrent = readNumber(r.pageCurrent ?? r.PageCurrent ?? 1);
  const pageSize = readNumber(r.pageSize ?? r.PageSize ?? 0);
  const total = readNumber(r.total ?? r.Total ?? items.length);
  const hasMore = readHasMore(r.hasMore ?? r.HasMore, pageCurrent, pageSize, total);

  return {
    mtctid: String(r.mtctid ?? r.Mtctid ?? ''),
    mucTieuName: String(r.mucTieuName ?? r.MucTieuName ?? ''),
    hasMore,
    pageCurrent: pageCurrent > 0 ? pageCurrent : 1,
    pageSize: pageSize > 0 ? pageSize : Math.max(items.length, 1),
    total: total > 0 ? total : items.length,
    items,
  };
};

const normalizeLegacyManagingStateList = (raw: unknown): EmployeeHomeManagingGoalGroup[] => {
  const list = Array.isArray(raw) ? raw : [];
  const items = list
    .map((item) => {
      const row = readRecord(item);
      if (row?.node ?? row?.Node) {
        return normalizeManagingList([item])[0];
      }
      if (row?.congViec ?? row?.CongViec) {
        return buildManagingItemFromNode(normalizeDuAnCongViecTreeNode(item));
      }
      return null;
    })
    .filter((item): item is EmployeeHomeManagingItem => item != null);

  return groupManagingItemsByGoal(items);
};

const normalizeManagingStateList = (raw: unknown): EmployeeHomeManagingGoalGroup[] => {
  const list = Array.isArray(raw) ? raw : [];
  if (!list.length) return [];
  if (isManagingGoalGroup(list[0])) {
    return list.map(normalizeManagingGoalGroup);
  }
  return normalizeLegacyManagingStateList(raw);
};

/** dangQuanLy: { overdue, today, upcoming, inProgress } — mỗi cột là nhóm theo mục tiêu. */
export const normalizeDangQuanLy = (raw: unknown): EmployeeHomeManagingSection => {
  if (Array.isArray(raw)) {
    return buildManagingSectionFromItems(normalizeManagingList(raw));
  }

  const r = readRecord(raw);
  if (!r) return emptyManagingSection();

  const section = emptyManagingSection();
  MANAGING_STATE_KEYS.forEach((state) => {
    section[state] = normalizeManagingStateList(readManagingStateField(r, state));
  });
  return section;
};

const normalizeSectionPagination = (raw: unknown) => {
  const r = readRecord(raw);
  if (!r) return undefined;
  return {
    hasMore: Boolean(r.hasMore ?? r.HasMore),
    pageCurrent: readNumber(r.pageCurrent ?? r.PageCurrent) || 1,
    pageSize: readNumber(r.pageSize ?? r.PageSize),
    total: readNumber(r.total ?? r.Total),
  };
};

const normalizeTheoMucTieu = (raw: unknown): EmployeeHomeMucTieuGroup[] => {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => {
    const r = readRecord(item) ?? {};
    const kpisRaw = (r.kpis ?? r.Kpis ?? []) as unknown[];
    return {
      mtctid: String(r.mtctid ?? r.Mtctid ?? ''),
      mucTieuName: String(r.mucTieuName ?? r.MucTieuName ?? ''),
      kpis: kpisRaw.map((kpi) => {
        const k = readRecord(kpi) ?? {};
        const mapped = mapBuoc6KpiToEmployeeHomeKpi(k);
        return {
          kpiID: mapped.kpiID || String(k.kpiID ?? k.KpiID ?? ''),
          kpiName: mapped.kpiName || String(k.kpiName ?? k.KpiName ?? ''),
          kpiTiTrongDatDuoc: mapped.kpiTiTrongDatDuoc ?? readNumber(k.kpiTiTrongDatDuoc ?? k.KpiTiTrongDatDuoc),
          chiTieuValue: mapped.chiTieuValue,
          thucHienValue: mapped.thucHienValue,
          idDoLuong: mapped.idDoLuong,
          donVi: mapped.donVi,
        };
      }),
      tasks: normalizeDuAnCongViecTreeList(r.tasks ?? r.Tasks),
      done: readNumber(r.done ?? r.Done),
      total: readNumber(r.total ?? r.Total),
      overdue: readNumber(r.overdue ?? r.Overdue),
      hasMore: Boolean(r.hasMore ?? r.HasMore),
      pageCurrent: readNumber(r.pageCurrent ?? r.PageCurrent) || 1,
      pageSize: readNumber(r.pageSize ?? r.PageSize),
      boPhanPhuTrachID: String(r.boPhanPhuTrachID ?? r.BoPhanPhuTrachID ?? '').trim() || undefined,
      boPhanPhuTrachName: String(r.boPhanPhuTrachName ?? r.BoPhanPhuTrachName ?? '').trim() || undefined,
    };
  });
};

export const normalizeTheoMucTieuSection = (raw: unknown): EmployeeHomeTheoMucTieuSection => {
  if (Array.isArray(raw)) {
    const items = normalizeTheoMucTieu(raw);
    return {
      items,
      total: items.length,
      pageCurrent: 1,
      pageSize: items.length,
      hasMore: false,
    };
  }

  const r = readRecord(raw);
  if (!r) return emptyTheoMucTieuSection();

  const items = normalizeTheoMucTieu(r.items ?? r.Items);
  const pagination = normalizeSectionPagination(r);
  return {
    items,
    total: pagination?.total ?? items.length,
    pageCurrent: pagination?.pageCurrent ?? 1,
    pageSize: pagination?.pageSize ?? items.length,
    hasMore: Boolean(pagination?.hasMore),
  };
};

export const normalizeManagerHomeResponse = (raw: unknown): ManagerHomeResponse | null => {
  const r = readRecord(raw);
  if (!r) return null;
  const canToggle = r.canToggleMtKhongPhuTrach ?? r.CanToggleMtKhongPhuTrach;
  return {
    stats: normalizeStats(r.stats ?? r.Stats),
    theoMucTieu: normalizeTheoMucTieu(r.theoMucTieu ?? r.TheoMucTieu),
    canToggleMtKhongPhuTrach: Boolean(canToggle),
  };
};

export const loadManagerEmployeeHome = async (
  params: LoadManagerEmployeeHomeParams
): Promise<ManagerHomeResponse | null> => {
  const {
    scopeId,
    idChoice,
    year,
    dateChoice,
    dateFilterMode,
    includeMtPhuTrach = true,
    includeMtKhongPhuTrach = true,
  } = params;

  const hasDateChoice = Boolean(String(dateChoice || '').trim());
  const res: { success?: boolean; data?: unknown; message?: string } =
    await actionPlanV2Api.getManagerHome({
      ID: scopeId,
      IDChoice: idChoice,
      Year: year,
      DateChoice: dateChoice || undefined,
      // D-H12: không ép 'created' — mặc định BE = giao khoảng như Home Cá nhân.
      DateFilterMode: hasDateChoice ? dateFilterMode : undefined,
      PhanHe: ACTION_PLAN_V2.PHAN_HE,
      IncludeMtPhuTrach: includeMtPhuTrach,
      IncludeMtKhongPhuTrach: includeMtKhongPhuTrach,
    });

  if (!res?.success || !res.data) return null;
  return normalizeManagerHomeResponse(res.data);
};

// #endregion loadManagerEmployeeHome

// #region normalizeEmployeeHome

const readCountMap = (raw: unknown): Record<string, number> => {
  const r = readRecord(raw);
  if (!r) return {};
  const out: Record<string, number> = {};
  Object.entries(r).forEach(([key, value]) => {
    const n = readNumber(value);
    if (n > 0) out[key] = n;
  });
  return out;
};

const normalizeStateCounts = (raw: unknown) => {
  const r = readRecord(raw);
  return {
    overdue: readNumber(r?.overdue ?? r?.Overdue),
    today: readNumber(r?.today ?? r?.Today),
    upcoming: readNumber(r?.upcoming ?? r?.Upcoming),
    inprogress: readNumber(r?.inprogress ?? r?.inProgress ?? r?.InProgress),
    completed: readNumber(r?.completed ?? r?.Completed),
  };
};

export const normalizeEmployeeHomeSummaryResponse = (
  raw: unknown
): EmployeeHomeSummaryResponse | null => {
  const r = readRecord(raw);
  if (!r) return null;

  return {
    stats: normalizeStats(r.stats ?? r.Stats),
    tasksTotal: readNumber(r.tasksTotal ?? r.TasksTotal),
    managedTotal: readNumber(r.managedTotal ?? r.ManagedTotal),
    goalsTotal: readNumber(r.goalsTotal ?? r.GoalsTotal),
    tasksByState: normalizeStateCounts(r.tasksByState ?? r.TasksByState),
    managedByState: normalizeStateCounts(r.managedByState ?? r.ManagedByState),
    tasksByMucTieuId: readCountMap(r.tasksByMucTieuId ?? r.TasksByMucTieuId),
    managedByMucTieuId: readCountMap(r.managedByMucTieuId ?? r.ManagedByMucTieuId),
    goalsByMucTieuId: readCountMap(r.goalsByMucTieuId ?? r.GoalsByMucTieuId),
  };
};

export const normalizeEmployeeHomeResponse = (raw: unknown): EmployeeHomeResponse | null => {
  const r = readRecord(raw);
  if (!r) return null;

  const legacyPagination = readRecord(r.theoMucTieuPagination ?? r.TheoMucTieuPagination);
  const theoMucTieuRaw = r.theoMucTieu ?? r.TheoMucTieu;
  const theoMucTieu = Array.isArray(theoMucTieuRaw)
    ? normalizeTheoMucTieuSection({
        items: theoMucTieuRaw,
        total: readNumber(legacyPagination?.total ?? legacyPagination?.Total),
        pageCurrent: readNumber(legacyPagination?.pageCurrent ?? legacyPagination?.PageCurrent),
        pageSize: readNumber(legacyPagination?.pageSize ?? legacyPagination?.PageSize),
        hasMore: Boolean(legacyPagination?.hasMore ?? legacyPagination?.HasMore),
      })
    : normalizeTheoMucTieuSection(theoMucTieuRaw);

  const mucTieuFilterRaw = r.mucTieuFilter ?? r.MucTieuFilter;
  const mucTieuFilterData = mapMucTieuFilterFromApiPayload(
    readRecord(mucTieuFilterRaw) ?? undefined
  );

  return {
    stats: normalizeStats(r.stats ?? r.Stats),
    canLamHomNay: normalizeCanLam(r.canLamHomNay ?? r.CanLamHomNay),
    dangQuanLy: normalizeDangQuanLy(r.dangQuanLy ?? r.DangQuanLy),
    theoMucTieu,
    mucTieuFilterData,
  };
};

export type EmployeeHomeLandingLoadResult = {
  tabData: EmployeeHomeResponse | null;
  summary: EmployeeHomeSummaryResponse | null;
  mucTieuFilterData: MucTieuFilterData | null;
};

/** Parse response IncludeLanding — preview 1 tab + summary unfiltered + MucTieuFilter tab. */
export const normalizeEmployeeHomeLandingResponse = (
  raw: unknown
): EmployeeHomeLandingLoadResult => {
  const r = readRecord(raw);
  if (!r) {
    return { tabData: null, summary: null, mucTieuFilterData: null };
  }

  const summary = normalizeEmployeeHomeSummaryResponse(r.summary ?? r.Summary);
  const theoMucTieu = normalizeTheoMucTieuSection(r.theoMucTieu ?? r.TheoMucTieu);
  const goalsTotal = summary?.goalsTotal ?? theoMucTieu.total ?? theoMucTieu.items.length;

  const mucTieuFilterData =
    mapMucTieuFilterFromApiPayload(
      readRecord(r.mucTieuFilter ?? r.MucTieuFilter) ?? undefined
    ) ?? null;

  const tabData: EmployeeHomeResponse = {
    stats: summary?.stats ?? { total: 0, done: 0, overdue: 0, inProgress: 0 },
    canLamHomNay: normalizeCanLam(r.canLamHomNay ?? r.CanLamHomNay),
    dangQuanLy: normalizeDangQuanLy(r.dangQuanLy ?? r.DangQuanLy),
    // REVIEW B-FE5: tổng của section (BE, đã lọc MT) là chuẩn; summary (chưa lọc) chỉ bù khi section thiếu tổng.
    theoMucTieu:
      !theoMucTieu.total && goalsTotal > 0
        ? {
            ...theoMucTieu,
            total: goalsTotal,
            hasMore: theoMucTieu.items.length < goalsTotal,
          }
        : theoMucTieu,
    mucTieuFilterData: mucTieuFilterData ?? undefined,
  };

  return {
    tabData,
    summary,
    mucTieuFilterData: mucTieuFilterData ?? tabData.mucTieuFilterData ?? null,
  };
};

// #endregion normalizeEmployeeHome
